import React, { useEffect, useRef, useState } from "react";
import { RotateCw } from "lucide-react";

const THRESHOLD = 70; // px pull to trigger refresh
const MAX_PULL = 120; // visual cap
const ARM_MIN_DY = 8; // dy in px before we promote touchmove to non-passive

/**
 * Pull-to-refresh wrapper for mobile PWA pages.
 *
 * Performance note: native scroll on mobile Safari/Chrome jitters if any
 * `touchmove` listener runs synchronously, especially with `{ passive: false }`.
 * To avoid that, we use a two-stage listener:
 *   1. Always attach a `passive: true` touchstart (cheap).
 *   2. Only attach a non-passive touchmove for the duration of a single
 *      gesture, AFTER we know the user is pulling down from the top.
 *   3. Detach on touchend/touchcancel so the document scrolls freely
 *      between gestures.
 *
 * This restores 60fps scrolling while preserving the pull-to-refresh feel.
 */
export default function PullToRefresh({ onRefresh, children }) {
  const startY = useRef(0);
  const armed = useRef(false); // promoted to non-passive listener?
  const moveHandlerRef = useRef(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const pullRef = useRef(0);

  useEffect(() => { refreshingRef.current = refreshing; }, [refreshing]);
  useEffect(() => { pullRef.current = pull; }, [pull]);

  useEffect(() => {
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (!isTouch) return;

    const cleanupMove = () => {
      if (moveHandlerRef.current) {
        window.removeEventListener("touchmove", moveHandlerRef.current, { passive: false });
        moveHandlerRef.current = null;
      }
      armed.current = false;
    };

    const onTouchStart = (e) => {
      if (refreshingRef.current) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (scrollTop > 4) return;
      startY.current = e.touches[0].clientY;

      // Don't attach the heavy listener yet — wait until we *see* downward pull.
      const onTouchMove = (ev) => {
        if (refreshingRef.current) return;
        const dy = ev.touches[0].clientY - startY.current;
        if (dy <= 0) {
          if (pullRef.current !== 0) setPull(0);
          return;
        }
        // Dampened pull
        const damped = Math.min(MAX_PULL, dy * 0.55);
        setPull(damped);
        if (dy > ARM_MIN_DY && ev.cancelable) {
          // We own the gesture now — suppress browser rubber-band.
          ev.preventDefault();
        }
      };
      moveHandlerRef.current = onTouchMove;
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      armed.current = true;
    };

    const onTouchEnd = async () => {
      if (!armed.current && !refreshingRef.current) return;
      const finalPull = pullRef.current;
      cleanupMove();
      if (finalPull >= THRESHOLD) {
        setRefreshing(true);
        setPull(THRESHOLD);
        try {
          await Promise.resolve(onRefresh?.());
        } catch { /* swallow */ }
        setRefreshing(false);
      }
      setPull(0);
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      cleanupMove();
    };
  }, [onRefresh]);

  const armedVisual = pull >= THRESHOLD;
  const indicatorOpacity = refreshing ? 1 : Math.min(1, pull / THRESHOLD);
  const iconRotation = refreshing ? 0 : Math.min(180, (pull / THRESHOLD) * 180);
  const isGesturing = armed.current;

  return (
    <>
      <div
        data-testid="pull-to-refresh-indicator"
        aria-hidden={pull === 0 && !refreshing}
        style={{
          transform: `translateY(${refreshing ? 12 : Math.max(0, pull - 36)}px)`,
          opacity: indicatorOpacity,
          transition: isGesturing ? "none" : "transform 220ms ease, opacity 220ms ease",
        }}
        className="fixed top-[72px] left-0 right-0 z-50 flex justify-center pointer-events-none"
      >
        <div className="paper px-4 py-2 flex items-center gap-2 shadow-sm">
          <RotateCw
            size={14}
            strokeWidth={2}
            className={refreshing ? "animate-spin text-[hsl(var(--primary))]" : "text-[hsl(var(--accent))]"}
            style={{
              transform: refreshing ? undefined : `rotate(${iconRotation}deg)`,
              transition: isGesturing ? "none" : "transform 180ms ease",
            }}
          />
          <span className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {refreshing ? "Refreshing…" : armedVisual ? "Release to refresh" : "Pull to refresh"}
          </span>
        </div>
      </div>

      <div
        style={{
          transform: `translateY(${refreshing ? THRESHOLD * 0.4 : pull * 0.4}px)`,
          transition: isGesturing ? "none" : "transform 220ms ease",
        }}
      >
        {children}
      </div>
    </>
  );
}
